/**
 * URL Management utilities for job ID routing
 */

/**
 * Validates if a job ID has the correct format
 * @param {string} jobId - Job ID to validate
 * @returns {boolean} True if valid format
 */
export const isValidJobId = (jobId) => {
  if (!jobId || typeof jobId !== 'string') {
    return false;
  }
  // Video job IDs can be either UUID format or 'vid_' prefixed format
  const uuidPattern = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
  const vidPattern = /^vid_[A-Z0-9]+$/;

  // Check if it matches either format
  return uuidPattern.test(jobId) || vidPattern.test(jobId);
};

/**
 * Extracts job ID from current URL path
 * @param {string} pathname - Current URL pathname
 * @returns {string|null} Job ID if found and valid, null otherwise
 */
export const getJobIdFromPath = (pathname) => {
  // Match pattern /creatives/job/{jobId} or /org/{orgSlug}/creatives/job/{jobId}
  const match = pathname.match(/^(?:\/org\/[^/]+)?\/creatives\/job\/(.+)$/);
  if (!match) {
    return null;
  }

  const jobId = match[1];
  return isValidJobId(jobId) ? jobId : null;
};

/**
 * Constructs the job URL path
 * @param {string} jobId - Valid job ID
 * @returns {string} URL path for the job
 */
export const getJobPath = (jobId) => {
  if (!isValidJobId(jobId)) {
    throw new Error(`Invalid job ID format: ${jobId}`);
  }
  return `/creatives/job/${jobId}`;
};

/**
 * Constructs the base creatives URL path
 * @returns {string} URL path for creatives base
 */
export const getCreativesBasePath = () => {
  return '/creatives';
};

/**
 * Determines if the current path is a job path
 * @param {string} pathname - Current URL pathname
 * @returns {boolean} True if on a job path
 */
export const isJobPath = (pathname) => {
  return /^(?:\/org\/[^/]+)?\/creatives\/job\/.+$/.test(pathname);
};

/**
 * Determines if the current path is the creatives base path
 * @param {string} pathname - Current URL pathname
 * @returns {boolean} True if on creatives base path
 */
export const isCreativesBasePath = (pathname) => {
  return pathname === '/creatives' || pathname === '/';
};

/**
 * Navigation helper functions (to be used with React Router's navigate)
 */
export const navigationHelpers = {
  /**
   * Navigate to job page
   * @param {function} navigate - React Router navigate function
   * @param {string} jobId - Job ID to navigate to
   */
  toJob: (navigate, jobId) => {
    navigate(getJobPath(jobId), { replace: false });
  },

  /**
   * Navigate to creatives base (clear job from URL)
   * @param {function} navigate - React Router navigate function
   * @param {boolean} replace - Whether to replace current history entry
   */
  toCreativesBase: (navigate, replace = true) => {
    navigate(getCreativesBasePath(), { replace });
  },

  /**
   * Update URL with job ID without changing other state
   * @param {function} navigate - React Router navigate function
   * @param {string} jobId - Job ID to add to URL
   */
  updateJobInUrl: (navigate, jobId) => {
    navigate(getJobPath(jobId), { replace: true });
  }
};

/**
 * Browser history utilities
 */
export const historyUtils = {
  /**
   * Check if browser supports History API
   * @returns {boolean} True if History API is supported
   */
  isSupported: () => {
    return !!(window.history && window.history.pushState);
  },

  /**
   * Add a listener for browser navigation events
   * @param {function} callback - Function to call on navigation
   * @returns {function} Cleanup function to remove listener
   */
  addNavigationListener: (callback) => {
    const handlePopState = (event) => {
      callback(window.location.pathname, event.state);
    };

    window.addEventListener('popstate', handlePopState);

    // Return cleanup function
    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }
};

/**
 * Debug utilities for development
 */
export const debugUtils = {
  /**
   * Log URL changes for debugging
   * @param {string} action - Description of the action
   * @param {string} from - Previous path
   * @param {string} to - New path
   * @param {string} jobId - Job ID if applicable
   */
  logNavigation: (action, from, to, jobId = null) => {
    if (process.env.NODE_ENV === 'development') {
      console.debug(`[URL Manager] ${action}:`, {
        from,
        to,
        jobId,
        timestamp: new Date().toISOString()
      });
    }
  }
};
